import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CONTACT_EMAIL, CONTACT_MAILTO, PAYMENTS, HAS_ONLINE_PAYMENTS } from '../siteConfig';

// Each payment method is scaffolded here. A method renders as an active
// button only when its link/address is set in siteConfig; otherwise it
// shows a quiet "coming soon" state so the page never reads as broken.
type Method = {
  key: string;
  name: string;
  role: string;          // billing-card style tag
  roleClass: string;
  blurb: string;
  href?: string;         // set → active button (link-based)
  cta?: string;          // button label for href methods
  address?: string;      // set → copyable value (Zelle contact, Bitcoin)
  addressLabel?: string; // small caption above a copyable value
};

const METHODS: Method[] = [
  {
    key: 'venmo',
    name: 'Venmo',
    role: 'Most used',
    roleClass: 'pay-card--primary',
    blurb: 'The quickest way for most families — tap below to pay the studio directly in the Venmo app.',
    href: PAYMENTS.venmoLink,
    cta: 'Pay with Venmo →',
  },
  {
    key: 'zelle',
    name: 'Zelle',
    role: 'No fees',
    roleClass: 'pay-card--alt',
    blurb: 'Free, bank-to-bank, and usually instant. In your banking app, send to the studio using the contact below.',
    address: PAYMENTS.zelleContact,
    addressLabel: 'Send Zelle to',
  },
  {
    key: 'stripe',
    name: 'Card — Stripe',
    role: 'Credit / debit',
    roleClass: 'pay-card--alt',
    blurb: 'Prefer to pay by card, or set up automatic monthly tuition? Pay securely through Stripe — no account needed.',
    href: PAYMENTS.stripePaymentLink,
    cta: 'Pay with card →',
  },
  {
    key: 'paypal',
    name: 'PayPal',
    role: 'Also accepted',
    roleClass: 'pay-card--alt',
    blurb: 'Prefer PayPal? Send payment to the studio directly from your PayPal balance or a linked card.',
    href: PAYMENTS.paypalMeLink,
    cta: 'Pay with PayPal →',
  },
  {
    key: 'bitcoin',
    name: 'Bitcoin',
    role: 'For fun',
    roleClass: 'pay-card--fun',
    blurb: 'Yes, really — the studio accepts Bitcoin. Send to the on-chain address below. (On-chain fees and confirmation times apply; ask before using this for time-sensitive payments.)',
    address: PAYMENTS.bitcoinAddress,
    addressLabel: 'On-chain address',
  },
];

export function Pay() {
  const [copied, setCopied] = useState(false);

  const copyAddress = (addr: string) => {
    navigator.clipboard?.writeText(addr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  return (
    <main className="stub pay">
      <Link to="/" className="stub__back">← Back to home</Link>
      <h2>Payments</h2>
      <p>
        Settle up for lessons here. Venmo and Zelle are the easiest for most
        families; card via Stripe, PayPal, and — for the adventurous — Bitcoin
        are also accepted.
      </p>

      {!HAS_ONLINE_PAYMENTS && (
        <p className="pay-setup-note">
          Online payment is being set up. For now, please arrange payment at
          your lesson or by email &mdash;{' '}
          <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>.
        </p>
      )}

      <div className="pay-grid">
        {METHODS.map(m => {
          const active = Boolean(m.href) || Boolean(m.address);
          return (
            <div key={m.key} className={`pay-card ${m.roleClass} ${active ? '' : 'pay-card--soon'}`}>
              <div className="pay-card__role kicker">{m.role}</div>
              <h3 className="pay-card__name">{m.name}</h3>
              <p className="pay-card__blurb">{m.blurb}</p>

              {/* link-based methods (Venmo, Stripe, PayPal) */}
              {m.href ? (
                <a
                  href={m.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--primary pay-card__cta"
                >
                  {m.cta ?? 'Pay →'}
                </a>
              ) : m.address ? (
                <div className="pay-card__btc">
                  {m.addressLabel && <span className="pay-card__addr-label kicker">{m.addressLabel}</span>}
                  <code className="pay-card__addr">{m.address}</code>
                  <button
                    type="button"
                    className="btn pay-card__cta"
                    onClick={() => copyAddress(m.address!)}
                  >
                    {copied ? 'Copied ✓' : 'Copy'}
                  </button>
                </div>
              ) : (
                <div className="pay-card__soon kicker">Coming soon</div>
              )}
            </div>
          );
        })}
      </div>

      <p className="pay-foot">
        Questions about billing, packages, or sibling rates?{' '}
        <a href={CONTACT_MAILTO}>Email the studio</a> or see the{' '}
        <Link to="/lesson-policies" className="kicker" style={{ letterSpacing: '0.08em' }}>
          lesson policies
        </Link>.
      </p>
    </main>
  );
}
